// src/pages/MesDemandes.jsx
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Package,
  Calendar,
  User,
  Eye,
  Ban,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  RefreshCw,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  Search,
  Filter,
  Truck
} from 'lucide-react';
import { API_URL } from '../../Config';

export default function MesDemandes() {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 15
  });
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const navigate = useNavigate();
  
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchDemandes = async (page = 1) => {
    setLoading(true);
    try {
      let url = `${API_URL}/my-demandes?page=${page}&per_page=${pagination.per_page}`;
      if (filterStatus) url += `&status=${filterStatus}`;

      const res = await axios.get(url, { headers });
      const data = res.data.data;

      setDemandes(data.data || []);
      setPagination({
        current_page: data.current_page || 1,
        last_page: data.last_page || 1,
        total: data.total || 0,
        per_page: data.per_page || 15
      });
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemandes(1);
  }, [filterStatus]);

  const getStatusInfo = (status) => {
    const config = {
      en_attente: {
        label: 'En attente',
        color: 'bg-yellow-100 text-yellow-800',
        icon: Clock,
        dot: 'bg-yellow-500'
      },
      valide: {
        label: 'Validée',
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle,
        dot: 'bg-green-500'
      },
      refuse: {
        label: 'Refusée',
        color: 'bg-red-100 text-red-800',
        icon: XCircle,
        dot: 'bg-red-500'
      },
      en_preparation: {
        label: 'En préparation',
        color: 'bg-indigo-100 text-indigo-800',
        icon: Package,
        dot: 'bg-indigo-500'
      },
      prete: {
        label: 'Prête à récupérer',
        color: 'bg-emerald-100 text-emerald-800',
        icon: Truck,
        dot: 'bg-emerald-500'
      },
      partiellement_recupere: {
        label: 'Partiellement récupérée',
        color: 'bg-blue-100 text-blue-800',
        icon: Truck,
        dot: 'bg-blue-500'
      },
      completement_recupere: {
        label: 'Récupérée',
        color: 'bg-teal-100 text-teal-800',
        icon: CheckCircle,
        dot: 'bg-teal-500'
      }
    };

    return config[status] || { 
      label: 'Inconnu', 
      color: 'bg-gray-100 text-gray-700', 
      icon: AlertCircle, 
      dot: '0' 
    };
  };

  const cancelDemande = async (id) => {
    if (!window.confirm('Annuler cette demande ?')) return;
    try {
      await axios.put(`${API_URL}/demandes/${id}/cancel`, {}, { headers });
      setDemandes(prev => prev.map(d => d.id === id ? { ...d, status: 'annulee' } : d));
    } catch {
      alert('Impossible d\'annuler');
    }
  };

  const formatDate = (date) => {
    try {
      return format(new Date(date), 'dd/MM/yy HH:mm', { locale: fr });
    } catch {
      return '-';
    }
  };

  const filteredDemandes = demandes.filter(d => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      d.id.toString().includes(s) ||
      (d.numero && d.numero.toLowerCase().includes(s)) ||
      (d.demandeur?.nom?.toLowerCase().includes(s))
    );
  });

  const isReadyToPickup = (demande) => {
    return ['prete', 'part_recupere'].includes(demande.status);
  };

  const isFullyRecovered = (demande) => demande.status === 'completement_recupere';

  if (loading && demandes.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Chargement de vos demandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen  p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Mes demandes de matériel</h1>
              <p className="text-gray-600 mt-1">
                {pagination.total} demande{pagination.total > 1 ? 's' : ''}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => fetchDemandes(pagination.current_page)}
                className="p-2 bg-white border border-gray-300 rounded-lg hover: transition"
                title="Actualiser"
              >
                <RefreshCw className="w-5 h-5" />
              </button>

              <Link
                to="/demandes/nouvelle"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition"
              >
                <Package className="w-4 h-4" />
                Nouvelle demande
              </Link>
            </div>
          </div>

          {/* Filtres */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher par numéro, nom..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-10 pr-3 py-2  border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="px-3 py-2  border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 outline-none"
              >
                <option value="">Tous les statuts</option>
                <option value="en_attente">En attente</option>
                <option value="valide">Validée</option>
                <option value="refuse">Refusée</option>
                <option value="en_preparation">En préparation</option>
                <option value="prete">Prête à récupérer</option>
                <option value="part_recupere">Partiellement récupérée</option>
                <option value="completement_recupere">Récupérée</option>
              </select>
            </div>
          </div>
        </div>

        {/* Liste des demandes */}
        <div className="space-y-3">
          {filteredDemandes.length === 0 ? (
            <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucune demande trouvée</p>
            </div>
          ) : (
            filteredDemandes.map(demande => {
              const status = getStatusInfo(demande.status);
              const Icon = status.icon;
              const articles = demande.demande_materiels || [];
              const totalQté = articles.reduce((a, i) => a + (i.quantite_demandee || 0), 0);

              return (
                <div key={demande.id} className="bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition">
                  <div className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Infos principales */}
                      <div className="flex-1">
                        <div className="flex items-center gap-4">
                          <div className="font-medium text-gray-900">
                            #{demande.numero || demande.id}
                          </div>
                          <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${status.color}`}>
                            <div className={`w-2 h-2 rounded-full ${status.dot}`}></div>
                            {status.label}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(demande.created_at)}
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {demande.demandeur?.nom || '—'}
                          </div>
                        </div>
                      </div>

                      {/* Articles count */}
                      <div className="text-center lg:text-right">
                        <div className="text-lg font-medium text-gray-900">{articles.length}</div>
                        <div className="text-sm text-gray-500">{totalQté} unité{totalQté > 1 ? 's' : ''}</div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/demande-timeline/${demande.id}`}
                          className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Eye className="w-4 h-4" />
                          Suivi
                        </Link>

                        {/* Bouton Récupérer */}
                        {isReadyToPickup(demande) && !isFullyRecovered(demande) && (
                          <Link
                            to={`/magasin/recuperer/${demande.id}`}
                            className="flex items-center gap-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg transition"
                          >
                            <Truck className="w-4 h-4" />
                            Récupérer
                          </Link>
                        )}

                        {/* Si déjà récupérée */}
                        {isFullyRecovered(demande) && (
                          <span className="flex items-center gap-1 px-3 py-2 bg-teal-100 text-teal-800 text-sm rounded-lg">
                            <CheckCircle className="w-4 h-4" />
                            Récupérée
                          </span>
                        )}

                        {/* Annuler si en attente */}
                        {demande.status === 'en_attente' && (
                          <button
                            onClick={() => cancelDemande(demande.id)}
                            className="p-2 text-red-600 hover:text-red-800"
                            title="Annuler"
                          >
                            <Ban className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {pagination.last_page > 1 && (
          <div className="mt-6 flex justify-center items-center gap-2">
            <button 
              disabled={pagination.current_page === 1} 
              onClick={() => fetchDemandes(pagination.current_page - 1)}
              className="p-2 border border-gray-300 rounded-lg hover: disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1 text-gray-700">
              Page {pagination.current_page} / {pagination.last_page}
            </span>
            <button 
              disabled={pagination.current_page === pagination.last_page} 
              onClick={() => fetchDemandes(pagination.current_page + 1)}
              className="p-2 border border-gray-300 rounded-lg hover: disabled:opacity-50"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}